<template>
  <v-app id="inspire">
    <v-main :class="{ 'app-main-container': store.state.isUserLoggedIn }">
      <view-mode-tooltip v-if="showViewOnlyTooltip && !isEmbedMode" />
      <v-container :fluid="true" :style="{ padding: isEmbedMode ? '0' : '0 40px', height: '100%' }">
        <template v-if="online">
          <router-view :key="refreshKey"/>
          <snackbar/>
          <v-overlay
            class="loading-overlay align-center justify-center"
            :class="{ 'loading-overlay-dimmed': loadingDimmed }"
            :model-value="loadingState || workflowLoadingState || loadingActiveSubscription"
            persistent
            @after-leave="store.commit('loading/setLoadingDimmed', false)"
          >
            <div class="loading-content">
              <v-progress-circular
                indeterminate
                size="60"
                width="5"
              />
              <div class="loading-message" v-if="loadingMessage || loadingDimmed" role="status">{{ loadingMessage }}</div>
            </div>
          </v-overlay>
          <v-dialog max-width="500" :modelValue="dialogState">
            <template v-slot:default>
              <v-card title="Warning">
                <v-card-text>
                  {{ dialogMessage }}
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn
                    variant="text"
                    @click="dialogClick('ok')"
                  >Ok
                  </v-btn>
                  <v-btn
                    variant="text"
                    @click="dialogClick('cancel')"
                  >Cancel
                  </v-btn>
                </v-card-actions>
              </v-card>
            </template>
          </v-dialog>
          <welcome-to-plan-dialog
            :is-open="store.state.payments.trialJustStarted || store.state.payments.campaignTrialJustClaimed"
            :plan="store.state.payments.campaignTrialJustClaimed ? 'welcome-back' : 'trial'"
            @close="closeWelcomeDialog"
          />
        </template>
        <template v-else>
          <div class="d-flex xs6 mt-2">
            <div class="text-h5 workflow-heading">Offline</div>
            <p>
              You appear to be offline! As soon as you are online again you
              will be reconnected.
            </p>
          </div>
        </template>
      </v-container>
    </v-main>

    <v-footer v-if="!isEmbedMode" color="grey-darken-3" :absolute="true" :app="true" class="pb-2 pt-2 app-footer">
      <span class="text-white text-right ml-4 mr-4">
        &copy; {{ new Date().getFullYear() }} {{ brand.name }}
      </span>
      <a
        v-if="brand.links.termsPdf"
        :href="brand.links.termsPdf"
        target="_blank"
        class="text-white text-center mr-4"
      >
        Terms of Service & Privacy Policy
      </a>
      <a
        v-if="brand.links.cookiePolicy"
        :href="brand.links.cookiePolicy"
        target="_blank"
        class="text-white text-center mr-4"
      >
        Cookie Policy
      </a>
      <a
        v-if="brand.analytics.measurementId"
        href="#"
        class="text-white text-center mr-4"
        data-cy="cookie-settings-link"
        @click.prevent="consentBanner?.open()"
      >
        Cookie settings
      </a>
    </v-footer>
    <cookie-consent-banner ref="consentBanner" />
    <!-- hidden div for testing web sockets with cypress -->
    <div style="display: none" data-test="websocket" :data-test-version="store.state.wsMessageForCypress?.version"/>
  </v-app>
</template>

<script setup>
import { ref, watch, computed, onMounted, getCurrentInstance, nextTick, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import Snackbar from '@/components/Snackbar.vue'
import WelcomeToPlanDialog from '@/components/WelcomeToPlanDialog.vue'
import CookieConsentBanner from '@/components/CookieConsentBanner.vue'
import { useBrand } from '@/composables/useBrand'
import ViewModeTooltip from '@/components/ViewModeTooltip.vue'
import projectApi from '@/api/projectApi'
import { EventBus } from '@/event-bus/event-bus.js'
import cognitoAuth from '@/cognito'
import { authHelpers } from '@/api/authHelpers'
import errorReporter from '@/utils/errorReporter'
import useAppTheme from '@/composables/useAppTheme'
import useCheckAppVersion from '@/composables/useCheckAppVersion'
import useBroadcastChannel from '@/composables/useBroadcastChannel'
import { useTrialEndingPrompt } from '@/composables/useTrialEndingPrompt'
import { getSubscriptionAccessType } from '@/utils/payments'
import { isEmbedMode as isEmbedRoute } from '@/utils/embed'
import { loginRoute } from '@/utils/loginRedirect'
import { syncHelpHeroIntegrations, syncHelpHeroUser, syncHelpHeroWorkspace } from '@/utils/helpHero'

const online = ref(true)
const refreshKey = ref(0)
const consentBanner = ref(null)

const store = useStore()
const router = useRouter()
const route = useRoute()
const { proxy } = getCurrentInstance()
const { setupBroadcastChannel } = useBroadcastChannel()
const brand = useBrand()
useAppTheme()
useCheckAppVersion()
useTrialEndingPrompt()

const loadingState = computed(() => store.getters['loading/getLoadingState'])
const workflowLoadingState = computed(() => store.getters['workflow/getLoadingState'])
const loadingActiveSubscription = computed(() => store.getters['loading/getLoadingActiveSubscription'])
const loadingMessage = computed(() => store.getters['loading/getLoadingMessage'])
const loadingDimmed = computed(() => store.getters['loading/getLoadingDimmed'])
const dialogState = computed(() => store.getters['dialog/getDialogState'])
const dialogMessage = computed(() => store.getters['dialog/getDialogMessage'])
const dialogCallback = computed(() => store.getters['dialog/getDialogCallback'])
const userInfo = computed(() => store.state.userInfo)
const adminUser = computed(() => store.state.adminUser)
// Embed mode (?embed=1): strips app chrome so a public board can be iframed (Miro-style).
const isEmbedMode = computed(() => isEmbedRoute(route))
const showViewOnlyTooltip = computed(
  () => !!route.params.projectId &&
    !!route.params.id &&
    !!store.state.workflow.isViewOnlyEnabled &&
    !workflowLoadingState.value
)

const dialogClick = (action) => {
  dialogCallback.value(action)
  store.commit('dialog/closeDialog')
}

const setProject = (item) => {
  router.push(`/dashboard/${item.id}`).catch(() => {
  })
}

const updateUserQueries = async (user) => {
  if (user) {
    // run listProjectMembershipsForCurrentUser first
    // because this one must be completed before we can
    // updateTeamMemberLastAccessToProject in the project
    // watcher. This is to avoid exceptions in Cypress
    proxy.$loadingState.start()

    try {
      await store.dispatch('projectMemberships/listProjectMembershipsForCurrentUser')
    } catch (error) {
      store.commit('logout')
      proxy.$loadingState.stop()
      router.push({ path: '/login' })
      return
    }

    const organisationsPromise = store.dispatch('organisations/listOrganisations')
    const getCurrentUserPromise = store.dispatch('user/getCurrentUser')
    await Promise.all([organisationsPromise, getCurrentUserPromise])
    proxy.$loadingState.stop()
  }
}

const updateProjectQueries = async (projectId) => {
  if (projectId) {
    const teamMembersPromise = store.dispatch('teamMembers/listTeamMembers', { projectId })
    const workflowsPromise = store.dispatch('workflows/listWorkflows', projectId)
    await Promise.all([teamMembersPromise, workflowsPromise])
    store.commit('projectMemberships/setActiveProjectId', projectId)
  }
}

const closeWelcomeDialog = () => {
  store.commit('payments/setTrialJustStarted', false)
  store.commit('payments/setCampaignTrialJustClaimed', false)
}

let lastSessionCheck = Date.now()
const SESSION_CHECK_INTERVAL = 5 * 60 * 1000 // 5 minutes

const onVisibilityChange = async () => {
  if (
    document.visibilityState === 'visible' &&
    store.state.isUserLoggedIn &&
    Date.now() - lastSessionCheck > SESSION_CHECK_INTERVAL
  ) {
    lastSessionCheck = Date.now()
    try {
      const session = await cognitoAuth.isAuthenticatedPromise()
      if (!session) {
        authHelpers.expireSession(null, 'tab-focus')
      }
    } catch (error) {
      console.warn('Session check failed, will retry on next tab focus:', error)
    }
  }
}

onMounted(() => {
  document.addEventListener('visibilitychange', onVisibilityChange)

  EventBus.$on('set-project', (item) => {
    setProject(item)
  })
  EventBus.$on('redirect-to-login', (returnPath) => {
    router.push(loginRoute(returnPath || route.fullPath))
  })
  EventBus.$on('refresh-dashboard', () => {
    // if the current route is the dashboard, refresh the page
    if (route.path === '/dashboard' || route.path === '/dashboard/') {
      refreshKey.value++
    }
  })

  setupBroadcastChannel((action, payload) => {
    switch (action) {
      case 'LOGOUT':
        if (payload?.loggedInUser === userInfo.value) {
          // If the user is logged in across multiple tabs within the same browser
          window.location.reload()
        }
        break
      default:
        console.warn(`Unknown action: ${action}`)
    }
  })
})

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', onVisibilityChange)
  EventBus.$off('set-project')
  EventBus.$off('redirect-to-login')
  EventBus.$off('refresh-dashboard')
})

watch(() => store.state.userId, async (user) => {
  if (user) {
    errorReporter.flush()
    try {
      await updateUserQueries(user)
    } catch (error) {
      console.log('error', error)
      store.commit('logout')
      proxy.$loadingState.stop()
      router.push({ path: '/login' })
    }
  }
}, { immediate: true })

let subscriptionCheckStartedForUserId = null

watch(() => store.state.user.user, async (user) => {
  if (!user) {
    subscriptionCheckStartedForUserId = null

    return
  }

  // The user is set more than once per login; checking only on the first run starts a trial at most once.
  if (subscriptionCheckStartedForUserId !== user.id) {
    subscriptionCheckStartedForUserId = user.id
    store.commit('loading/setLoadingActiveSubscription', true)
    await store.dispatch('payments/getPayerUserSubscription')
    const activeSubscription = await store.dispatch('payments/getActiveSubscription', { withTrialStart: true })
    const subscriptionAccessType = getSubscriptionAccessType(activeSubscription)
    const shouldAccessAdmin = adminUser.value && route.path?.startsWith('/admin')

    if (subscriptionAccessType === 'NO_ACCESS' && !shouldAccessAdmin) {
      router.push({ path: '/plans' })
    }
    store.commit('loading/setLoadingActiveSubscription', false)
  }
  syncHelpHeroUser(store.state)
})

watch(() => store.state.github.githubAccounts, () => {
  syncHelpHeroIntegrations(store.state)
})

watch(() => store.state.organisations.organisations, () => {
  syncHelpHeroWorkspace(store.state)
})

watch(() => route.params.projectId, async (projectId) => {
  if (projectId && store.state.isUserLoggedIn) {
    try {
      proxy.$loadingState.start()
      await updateProjectQueries(projectId)
      syncHelpHeroWorkspace(store.state)
      await nextTick(() => {
        projectApi.updateTeamMemberLastAccessToProject(projectId)
      })
      proxy.$loadingState.stop()
    } catch (error) {
      console.log(error)
      store.commit('logout')
      proxy.$loadingState.stop()
      router.push({ path: '/login' })
    }
  } else {
    store.commit('projectMemberships/setActiveProjectId', null)
  }
}, { immediate: true })
</script>

<style scoped>
#app {
  min-width: 1280px;
}

.v-toolbar__title {
  margin-left: 0;
}

.app-footer {
  z-index: 7 !important;
}

.projects-list-menu {
  max-height: calc(100vh - 20px);
}
</style>

<style>

:root {
  --blue-1: #209AD6;
  --blue-2: #C3DAF6;
  --blue-3: #4285F4;
  --blue-4: #0D66D0;
  --red-1: #BA274A;
  --red-2: #e6899f;
  --background: #FAF7F5;
  --text-light: #4F5359;
  --text-dark: #1F2833;
  --light-1: #FCFCFC;
  --light-2: #F5F2F0;
  --light-3: #E5E3E1;
  --light-4: #CFD3D9;
  --light-5: #C8CCCA;
  --light-6: #D4D9D7;
  --dark-1: #A3ADB9;
  --dark-2: #878F99;
  --dark-3: #61676E;
  --dark-4: #363A40;
  --dark-5: #1C1F1D;
  --card-1: #9C2BF6;
  --card-2: #FFA26E;
  --card-3: #5ECEF8;
  --card-4: #5E5E38;
  --card-4-alt: #EE7878;
  --card-5: #C04DCA;
  --card-6: #FFA26E;
  --card-7: #4957D4;
  --card-8: #64AB43;
  --card-4-alt2: #BF4965;
  --event-orange: #FFC28D;
  --event-stroke-orange: #B2885D;
  --event-yellow: #FFEB90;
  --event-stroke-yellow: #CCBE7B;
  --event-green: #BDEE7A;
  --event-stroke-green: #88AB78;
  --event-turquoise: #B7EEE4;
  --event-stroke-turquoise: #82BAB0;
  --event-light-blue: #A7EEE1;
  --event-stroke-light-blue: #709FBA;
  --event-purple: #C1C0F4;
  --event-stroke-light-blue-alt: #8786C;
  --event-pink: #ECC0F4;
  --event-stroke-pink: #B986C2;

  --color-primary: #022a44;
  --light-blue: #3183fb;
  --white: #ffffff;
  --plan-card-basic: #3BB0E6;
  --plan-card-pro: #A0F090;
  --plan-card-enterprise: #A091FF;

  --diagram-stroke: #1F2833;
  --diagram-border: #A3ADB9;
  --diagram-lane-divider: #d7dbe0;
  --diagram-group-header: #CFD3D9;
  --surface-primary: #FCFCFC;
  --surface-secondary: #F5F2F0;
  --surface-elevated: #FFFFFF;

  --btn-subtle: var(--light-1);
  --btn-subtle-hover: var(--light-2);
  --btn-subtle-active: var(--light-3);

  font-family: Inter, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18.2px;
  letter-spacing: -0.42px !important;
}

[data-theme="dark"] {
  --blue-1: #4DB8E8;
  --blue-2: #2A4A6E;
  --blue-3: #5A9DF5;
  --blue-4: #3A8AE8;
  --red-1: #D4506E;
  --red-2: #8A3A4E;
  --background: #121518;
  --text-light: #B0B8C4;
  --text-dark: #E8EAF0;
  --light-1: #1E2228;
  --light-2: #252A31;
  --light-3: #2E343C;
  --light-4: #3A424D;
  --light-5: #4A5260;
  --light-6: #3F4750;
  --dark-1: #6B7585;
  --dark-2: #8892A0;
  --dark-3: #A0A8B4;
  --dark-4: #C8CED6;
  --dark-5: #E8EAF0;
  --card-1: #7A22C4;
  --card-2: #CC8258;
  --card-3: #4BA5C8;
  --card-4: #4A4A2E;
  --card-4-alt: #C06060;
  --card-5: #9A3DA2;
  --card-6: #CC8258;
  --card-7: #3A45A8;
  --card-8: #508A36;
  --card-4-alt2: #9A3A50;
  --event-orange: #C89870;
  --event-stroke-orange: #8A6848;
  --event-yellow: #C8C078;
  --event-stroke-yellow: #9A9060;
  --event-green: #90B870;
  --event-stroke-green: #688858;
  --event-turquoise: #88B8B0;
  --event-stroke-turquoise: #608A82;
  --event-light-blue: #80B8A8;
  --event-stroke-light-blue: #587A90;
  --event-purple: #9898C4;
  --event-stroke-light-blue-alt: #68688A;
  --event-pink: #B898C4;
  --event-stroke-pink: #8A6898;

  --color-primary: #4DA8E8;
  --light-blue: #5A9AE8;
  --white: #1E2228;
  --plan-card-basic: #2E8AB8;
  --plan-card-pro: #78C068;
  --plan-card-enterprise: #8078D0;

  --diagram-stroke: #B0B8C4;
  --diagram-border: #4A5260;
  --diagram-lane-divider: #3A424D;
  --diagram-group-header: #2E343C;
  --surface-primary: #1E2228;
  --surface-secondary: #252A31;
  --surface-elevated: #2A3038;

  --btn-subtle: transparent;
  --btn-subtle-hover: rgba(255, 255, 255, 0.08);
  --btn-subtle-active: rgba(255, 255, 255, 0.14);
}

@font-face {
  font-family: 'Faktum';
  src: url('./assets/fonts/Faktum-Bold.otf') format('opentype');
  font-weight: bold;
  font-style: normal;
}

@font-face {
  font-family: 'Faktum';
  src: url('./assets/fonts/Faktum-Regular.otf') format('opentype');
  font-weight: normal;
  font-style: normal;
}

.text-h2 {
  font-family: 'Inter', sans-serif !important;
}

.text-h4 {
  font-family: 'Inter', sans-serif !important;
}

.text-h5 {
  font-family: 'Inter', sans-serif !important;
}

.text-h6 {
  font-family: 'Inter', sans-serif !important;
}

.text-body-1 {
  font-family: 'Inter', sans-serif !important;
}

.text-caption {
  font-family: 'Inter', sans-serif !important;
}

.v-application {
  background-color: var(--background) !important;
}

.v-field {
  font-size: 14px !important;
}

.v-btn:not(.v-btn--icon),
.v-dialog,
.v-tooltip__content,
.v-menu__content,
.v-card:not(.v-card--flat),
.v-expansion-panels {
  border-radius: 10px !important;
}

.v-card-text {
  font-size: 1rem !important;
}

.v-expansion-panel:first-child {
  border-radius: 10px 10px 0 0;
}

.v-expansion-panel:last-child {
  border-radius: 0 0 10px 10px;
}

.v-toolbar {
  background-color: var(--light-1, #FCFCFC) !important;
  color: var(--text-dark) !important;
}

.v-card-actions {
  gap: 12px;
  margin: 0;
  padding: 16px !important;
}

.v-dialog > .v-overlay__content > .v-card > .v-card-text {
  letter-spacing: -0.42px !important;
}

/* Override always uppercase */
.v-btn {
  text-transform: none !important;
  letter-spacing: -0.42px !important;
}

/* Custom styling for v-switch */
.v-switch__track {
  opacity: 0.24 !important;
  background-color: var(--dark-1) !important;
}

.v-switch__thumb {
  background-color: var(--dark-2) !important;
}

.v-selection-control__input::before {
  display: none;
}

.v-selection-control__input:hover > .v-switch__thumb {
  background-color: var(--dark-3) !important;
}

/* Buttons used at bottom of containers */
div.btn-container-bottom {
  display: inline-block;
  padding: 3px 8px;
  margin-right: 4px;
  border-radius: 0 0 4px 4px;
  background: var(--light-1);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.15);
  color: var(--text-light);
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;

  &.disabled {
    pointer-events: none;
  }

  &:hover {
    background-color: var(--light-2);
  }

  &:active {
    background-color: var(--light-2);
  }

  & > .v-icon {
    width: 20px;
    height: 20px;
  }
}

[data-theme="dark"] div.btn-container-bottom {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.15), inset 0 0 0 1px var(--light-5);
}

/* Custom styling for default/small size of variant default (elevated) buttons */
.v-btn.v-btn--icon.v-btn--size-small.v-btn--variant-elevated {
  width: 24px;
}

.v-btn.v-btn--size-small.v-btn--variant-elevated {
  height: 24px;
}

.v-btn.v-btn--icon.v-btn--size-default.v-btn--variant-elevated {
  width: 32px;
}

.v-btn.v-btn--size-default.v-btn--variant-elevated {
  height: 32px;
}

.v-btn.v-btn--size-default.v-btn--variant-elevated,
.v-btn.v-btn--size-small.v-btn--variant-elevated {
  border-radius: 3px !important;
  opacity: 1 !important;
  color: var(--text-dark);
  padding: 0 8px;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  background: var(--btn-subtle);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
  letter-spacing: -0.42px;
  transition: background-color ease 0.2s;
}

.v-btn.v-btn--size-default.v-btn--variant-elevated:hover,
.v-btn.v-btn--size-small.v-btn--variant-elevated:hover {
  opacity: 1 !important;
  background-color: var(--btn-subtle-hover);
}

.v-btn.v-btn--size-default.v-btn--variant-elevated:disabled,
.v-btn.v-btn--size-small.v-btn--variant-elevated:disabled {
  opacity: 0.3 !important;
}

.v-btn.v-btn--size-default.v-btn--variant-elevated:active,
.v-btn.v-btn--size-small.v-btn--variant-elevated:active {
  opacity: 1 !important;
  background-color: var(--btn-subtle-active);
}

/* Custom styling for large/default/small size of variant flat buttons */
.v-btn.v-btn--variant-flat {
  background-color: var(--dark-4) !important;
  color: var(--light-1) !important;
}

.v-btn.v-btn--size-large.v-btn--variant-flat {
  height: 42px !important;
  padding: 6px 12px !important;
  border-radius: 8px !important;
  font-weight: 500 !important;
  font-size: 16px !important;
}

.v-btn.v-btn--size-small.v-btn--variant-flat {
  padding: 2px 20px;
  border-radius: 5px;
  color: var(--light-1);
  font-size: 14px;
  font-weight: 500;
  line-height: 14px;
}

.v-btn.v-btn--icon.v-btn--size-x-small.v-btn--variant-flat {
  width: 20px !important;
  max-width: 20px !important;
  min-width: 20px !important;
}

.v-btn.v-btn--size-x-small.v-btn--variant-flat {
  height: 20px !important;
  padding: 2px 2px;
  border-radius: 5px;
  color: var(--light-1);
  font-size: 11px;
  font-weight: 500;
  line-height: 14px;
}

.v-btn.v-btn--variant-flat:hover {
  background-color: var(--dark-3) !important;
}

.v-btn.v-btn--variant-flat:active {
  background-color: var(--dark-2) !important;
}

/* TODO: add default size */

/* Custom styling for large size of variant outlined buttons */
.v-btn.v-btn--size-large.v-btn--variant-outlined {
  border-radius: 8px !important;
  padding: 11px 12px !important;
  height: 42px !important;
  font-weight: 500 !important;
  font-size: 16px !important;
}

.v-btn.v-btn--size-small.v-btn--variant-outlined {
  border-radius: 8px !important;
  border: 1px solid var(--dark-2) !important;
  padding: 5px 8px 5px 12px !important;
  height: 30px !important;
  font-weight: 500 !important;
  font-size: 14px !important;
}

/* Custom styling for small/default plain icon buttons */
.v-btn.v-btn--icon.v-btn--size-small.v-btn--variant-plain {
  width: 16px !important;
  height: 16px !important;
  padding: 0 !important;
  font-size: 10.65px !important;
}

.v-btn.v-btn--icon.v-btn--size-default.v-btn--variant-plain {
  width: 24px !important;
  height: 24px !important;
  font-size: 13.5px !important;
}

/* .v-btn.v-btn--icon.v-btn--size-small.v-btn--variant-plain > .v-btn__content {
  width: 16px !important;
  height: 16px !important;
} */

/* Custom styling for round icon buttons */
.v-btn.v-btn--icon.v-btn--variant-text {
  width: 48px !important;
}

.v-btn.v-btn--variant-text {
  height: 48px !important;
  font-size: 16px !important;
  color: var(--text-light) !important;
}

.v-btn.v-btn--size-small.v-btn--icon.v-btn--variant-text {
  width: 40px !important;
}

.v-btn.v-btn--size-small.v-btn--variant-text {
  height: 40px !important;
}

.v-btn.v-btn--size-small.v-btn--variant-text {
  font-size: 16px !important;
}

.v-btn.v-btn--icon.v-btn--size-x-small.v-btn--variant-text {
  width: 32px !important;
  max-width: 32px !important;
  min-width: 32px !important;
}

.v-btn.v-btn--size-x-small.v-btn--variant-text {
  height: 32px !important;
  font-size: 13.5px !important;
}

.v-btn.v-btn--variant-text:hover {
  background-color: var(--light-2) !important;
}

.v-btn.v-btn--variant-text:active {
  background-color: var(--light-3) !important;
}

.v-btn.v-btn--active.v-btn--variant-text {
  background-color: var(--light-3) !important;
}

.v-btn.top-nav.v-btn--variant-text:active {
  background-color: var(--blue-2) !important;
}

.v-btn.top-nav.v-btn--active.v-btn--variant-text {
  background-color: var(--blue-2) !important;
}

.v-btn.v-btn--size-small.v-btn--variant-text:active {
  background-color: var(--dark-1) !important;
}

.v-btn.v-btn--size-small.v-btn--variant-text:active {
  background-color: var(--dark-1) !important;
}

/* Custom styling for default/ large tonal buttons (with support for icon and text) */
.v-btn.v-btn--size-default.v-btn--variant-tonal > .v-btn__underlay {
  display: none
}

.v-btn.v-btn--size-large.v-btn--variant-tonal > .v-btn__underlay {
  display: none
}

.v-btn.v-btn--size-default.v-btn--variant-tonal > .v-btn__content > .v-icon {
  line-height: 18px;
}

.v-btn.v-btn--icon.v-btn--size-default.v-btn--variant-tonal {
  width: 32px;
}

.v-btn.v-btn--size-default.v-btn--variant-tonal {
  height: 32px;
}

.v-btn.v-btn--size-default.v-btn--variant-tonal {
  height: 32px;
  background-color: var(--btn-subtle);
  border-radius: 3px !important;
  opacity: 1 !important;
  color: var(--text-dark);
  padding: 0 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 18.2px;
  letter-spacing: -0.42px;
  transition: background-color ease 0.2s;
}

.v-btn.v-btn--size-large.v-btn--variant-tonal {
  padding: 6px 12px !important;
  height: 42px !important;
  font-size: 16px;
  font-weight: 500;
  border-radius: 8px !important;
  background-color: var(--btn-subtle);
}

.v-btn.v-btn--size-default.v-btn--variant-tonal:hover {
  opacity: 1 !important;
  background-color: var(--btn-subtle-hover);
}

.v-btn.v-btn--size-default.v-btn--variant-tonal:disabled {
  opacity: 0.3 !important;
}

.v-btn.v-btn--variant-tonal:active {
  opacity: 1 !important;
  background-color: var(--btn-subtle-active);
}

/* Custom styling for v-text-field with variant solo-filled */
.v-field {
  text-transform: none !important;
}

.v-field.v-field--variant-solo-filled {
  background-color: transparent !important;
  box-shadow: none;
  border-radius: 8px;
  border: 1px solid var(--light-4);
}

.v-field.v-field--active.v-field--variant-solo-filled {
  border-radius: 10px !important;
  border: 1px solid var(--dark-2);
}

/* No more ripples */
.v-ripple__container {
  display: none;
}

/* Change default background of cards with variant elevated */
.v-card--variant-elevated {
  background-color: var(--light-1) !important;
}

/* Custom styling for v-field with variant solo */
.v-field.v-field--variant-solo {
  background-color: var(--light-1) !important;
}

/* Custom styling for v-list */
.v-list {
  background-color: var(--light-1, #FCFCFC) !important;
}

.v-list-item {
  background-color: var(--light-1, #FCFCFC) !important;
  padding: 8px 16px !important;
  border-radius: 8px !important;
  transition: background-color 0.15s ease-in-out !important;
}

/* Custom styling for v-list inside v-menu overlay */
.v-menu > .v-overlay__content > .v-list {
  padding: 8px !important;
  border-radius: 10px !important;
  /* background-color: var(--white, #FFF) !important; */
  box-shadow: 8px 4px 24px 0px rgba(0, 0, 0, 0.24) !important;
}

[data-theme="dark"] .v-menu > .v-overlay__content > .v-list,
[data-theme="dark"] .v-menu > .v-overlay__content > .v-card,
[data-theme="dark"] .v-menu > .v-overlay__content > .diagram-legend {
  background-color: var(--surface-elevated) !important;
  border: 1px solid var(--light-5);
}

[data-theme="dark"] .v-menu .v-list,
[data-theme="dark"] .v-menu .v-list-item:not(:hover):not(:active) {
  background-color: transparent !important;
}

[data-theme="dark"] .v-menu .v-list-item:hover {
  background-color: var(--btn-subtle-hover) !important;
}

[data-theme="dark"] .v-menu .v-list-item:active {
  background-color: var(--btn-subtle-active) !important;
}

[data-theme="dark"] .v-dialog > .v-overlay__content > .dialog,
[data-theme="dark"] .v-dialog > .v-overlay__content > .v-card,
[data-theme="dark"] .v-dialog > .v-overlay__content > form > .v-card,
[data-theme="dark"] .v-dialog > .v-overlay__content > div > .v-card {
  background-color: var(--surface-elevated) !important;
  border-color: var(--surface-elevated) !important;
  box-shadow: inset 0 0 0 1px var(--light-5), 0 1px 4px 0 rgba(0, 0, 0, 0.15) !important;
}

.v-menu > .v-overlay__content > .v-list > .v-list-item {
  /* background-color: var(--white, #FFF) !important; */

  &.releaseFilter, &.sort-item {
    .v-list-item__overlay {
      opacity: 0 !important;
    }

    &.active {
      background-color: var(--btn-subtle-active) !important;
    }
  }
}

.v-list-item__prepend > .v-icon {
  opacity: 1 !important;
}

.v-list-item__prepend > .v-icon ~ .v-list-item__spacer {
  width: 8px !important;
}

.v-list-item:hover > .v-list-item__overlay {
  opacity: 0 !important;
}

.v-list-item:hover {
  background-color: var(--light-2) !important;
}

.v-list-item:active {
  background-color: var(--light-3) !important;
}

/* Custom styling for v-tab */
.new-tab {
  height: 34px !important;
  border-radius: 8px;
  background-color: var(--light-2);
}

.new-tab.large {
  height: 38px !important;
}

.v-slide-group__content {
  justify-content: space-between;
}

.v-btn.v-tab {
  width: auto !important;
  height: 34px !important;
  padding: 0 8px !important;
  border-radius: 8px !important;
  font-size: 14px !important;
  background-color: transparent !important;
}

.new-tab.large .v-btn.v-tab {
  height: 38px !important;
}

.v-btn.v-tab > .v-btn__content {
  color: var(--text-light);
  font-size: 14px;
  font-weight: 400;
  line-height: 14px;
}

.v-btn.v-tab.v-tab--selected > .v-btn__content {
  color: var(--light-1);
  font-weight: 500;
}

.v-tab__slider {
  z-index: -1;
  background-color: var(--dark-4);
  color: var(--text-light);
  height: 34px !important;
  border-radius: 8px !important;
}

.new-tab.large .v-tab__slider {
  height: 38px !important;
}

.v-btn.v-tab.v-tab--selected:hover {
  color: var(--light-1) !important;
  background-color: transparent !important;
}

.text-title {
  color: var(--text-dark);
  font-size: 20px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.6px;
}

.text-subtitle {
  color: var(--text-dark);
  font-size: 16px;
  line-height: 120%;
  letter-spacing: -0.6px;
}

.round-bottom,
.round-bottom .v-table,
.round-bottom .v-data-table {
  border-radius: 0 0 10px 10px !important;
}

.v-table {
  background: transparent !important;
}

th {
  height: 36px !important;
}

th > .v-data-table-header__content {
  color: var(--text-dark);
  font-size: 12px;
  font-weight: 600;
  line-height: 100%; /* 12px */
  letter-spacing: -0.36px;
}

td {
  height: 48px;
  color: var(--text-light);
  font-size: 16px;
  line-height: 120%; /* 19.2px */
  letter-spacing: -0.48px;
}

.v-tabs-bar {
  border-radius: 10px 10px 0 0;
}

.loading-overlay {
  z-index: 20001 !important;
}

/* Fixed black, not Vuetify's default on-surface scrim, which inverts to a light wash in dark mode. */
.loading-overlay .v-overlay__scrim {
  background: #000;
  opacity: 0.2;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
}

.loading-content .v-progress-circular {
  color: rgba(var(--v-theme-on-surface), 0.72);
}

.loading-message {
  font-size: 15px;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.85);
}

/* Opt-in via loading/setLoadingDimmed. Other loaders, like AI generation, keep the page visible
   so the user can watch it change. Tint via the background, since opacity would also fade the blur. */
.loading-overlay-dimmed .v-overlay__scrim {
  opacity: 1;
  background: rgba(0, 0, 0, 0.55);
  -webkit-backdrop-filter: blur(3px);
  backdrop-filter: blur(3px);
}

.loading-overlay-dimmed .loading-content {
  gap: 22px;
}

.loading-overlay-dimmed .v-progress-circular,
.loading-overlay-dimmed .loading-message {
  color: #FFFFFF;
}

/* Two lines reserved, so a message that wraps doesn't re-centre the block and shift the spinner. */
.loading-overlay-dimmed .loading-message {
  font-size: 16px;
  font-weight: 500;
  max-width: 300px;
  min-height: 3em;
  line-height: 1.5;
  text-align: center;
}

.show-on-hover {
  transition: opacity 0.15s ease-in-out !important;
  opacity: 0 !important;
}

.show-on-hover:hover {
  opacity: 1 !important;
}

.hover-container:hover + .show-on-hover {
  opacity: 1 !important;
}

.hover-container:hover > div .show-on-hover {
  opacity: 1 !important;
}

.hover-container:hover > .show-on-hover {
  opacity: 1 !important;
}

.dialog-top-right {
  top: 0;
  right: 0;
  margin: 0 !important;
}

.card {
  border-radius: 12px;
  background: var(--light-1);
  border: 1px solid var(--light-1);
  padding: 12px 16px 16px !important;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15) !important;
}

.opacity-100 {
  opacity: 1 !important;
}

.opacity-0 {
  opacity: 0 !important;
}

.fit-content {
  width: fit-content !important;
}

.checkbox-container {
  width: 16px;
  height: 16px;
  cursor: pointer;
  align-items: center;
  border-radius: 2px;
  display: inline-flex;
  justify-content: center;
  border: 1px solid var(--text-light, #4F5359);

  &:not(.checked) {
    > * {
      display: none;
    }
  }

  &.checked {
    color: var(--light-1, #FCFCFC);
    border-color: var(--text-light, #4F5359);
    background-color: var(--text-light, #4F5359);
  }
}

.auth-card-wrapper {
  padding-top: 133px;
  padding-bottom: 124px;
}

.auth-card-wrapper .v-field:has(input:autofill) .v-label.v-field-label {
  visibility: hidden !important;
}

.auth-card-wrapper .v-field:has(input:autofill) .v-label.v-field-label--floating {
  visibility: visible !important;
}

.auth-card-wrapper .v-field--variant-outlined:has(input:autofill) .v-field__outline__notch::before {
  opacity: 0;
}

.auth-card-wrapper .v-field:has(input:autofill) .v-field__append-inner {
  background-color: var(--autofill-bg, rgb(232, 240, 254));
  margin-inline-end: -12px;
  padding-inline-end: 12px;
  border-start-end-radius: 4px;
  border-end-end-radius: 4px;
}

[data-theme="dark"] .auth-card-wrapper {
  --autofill-bg: rgba(70, 90, 126, 0.4);
}

.auth-card {
  width: 444px;
  margin: 0 auto;
  border-radius: 12px;
  background: var(--light-1);
  padding: 12px 16px 16px;
  border: 1px solid var(--light-1);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);

  .auth-card-container {
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .auth-card-footer {
    display: flex;
    align-items: center;

    .auth-card-footer-part {
      gap: 10px;
      display: flex;
      align-items: center;

      &.auth-card-footer-part_column {
        flex-direction: column;
        align-items: flex-start;
      }
    }
  }
}

.height-transition-hover-container {
  .height-transition-container {
    height: 0;
    transition: all 0.2s;

    .show-on-hover-item {
      opacity: 0;
    }
  }

  &:hover {
    .height-transition-container {
      height: var(--transitionContainerHeight, 20px);

      .show-on-hover-item {
        opacity: 1;

        &.v-btn--variant-plain {
          opacity: 0.7;

          &:hover {
            opacity: 1;
          }
        }

        &.v-btn--disabled {
          opacity: 0.26;
        }
      }
    }
  }
}

.v-btn.bg-deep-purple-accent-4 {
  background-color: var(--card-7) !important;
}

html.resizing * {
  cursor: col-resize !important;
}

html.theme-switching,
html.theme-switching *,
html.theme-switching *::before,
html.theme-switching *::after {
  transition: none !important;
}

[data-theme="dark"] a:not([class]):not(:has(*)) {
  color: var(--blue-3);
}

[data-theme="dark"] .text-black {
  color: var(--text-dark) !important;
}

[data-theme="dark"] .text-grey-darken-3 {
  color: var(--dark-4) !important;
}

[data-theme="dark"] .text-grey-darken-4 {
  color: var(--dark-5) !important;
}

[data-theme="dark"] .bg-grey-lighten-3 {
  background-color: var(--light-3) !important;
}

[data-theme="dark"] .ql-toolbar.ql-snow {
  border-color: var(--light-4);
}

[data-theme="dark"] .ql-container.ql-snow {
  border-color: var(--light-4);
  color: var(--text-dark);
}

[data-theme="dark"] .ql-editor {
  color: var(--text-dark);
}

[data-theme="dark"] .ql-snow .ql-stroke {
  stroke: var(--text-light);
}

[data-theme="dark"] .ql-snow .ql-fill {
  fill: var(--text-light);
}

[data-theme="dark"] .ql-snow .ql-picker {
  color: var(--text-light);
}

[data-theme="dark"] .ql-snow .ql-picker-options {
  background-color: var(--light-2);
}

.theme-fill-event-peach {
  fill: rgb(var(--v-theme-event-peach));
  stroke: rgb(var(--v-theme-event-peach));
  fill-opacity: 1;
}

.theme-fill-event-yellow {
  fill: rgb(var(--v-theme-event-yellow));
  stroke: rgb(var(--v-theme-event-yellow));
  fill-opacity: 1;
}

.theme-fill-event-green {
  fill: rgb(var(--v-theme-event-green));
  stroke: rgb(var(--v-theme-event-green));
  fill-opacity: 1;
}

.theme-fill-event-teal {
  fill: rgb(var(--v-theme-event-teal));
  stroke: rgb(var(--v-theme-event-teal));
  fill-opacity: 1;
}

.theme-fill-event-blue {
  fill: rgb(var(--v-theme-event-blue));
  stroke: rgb(var(--v-theme-event-blue));
  fill-opacity: 1;
}

.theme-fill-event-lavender {
  fill: rgb(var(--v-theme-event-lavender));
  stroke: rgb(var(--v-theme-event-lavender));
  fill-opacity: 1;
}

.theme-fill-event-pink {
  fill: rgb(var(--v-theme-event-pink));
  stroke: rgb(var(--v-theme-event-pink));
  fill-opacity: 1;
}

.theme-fill-event-gray {
  fill: rgb(var(--v-theme-event-gray));
  stroke: rgb(var(--v-theme-event-gray));
  fill-opacity: 1;
}

.theme-text-event-peach,
.theme-text-event-yellow,
.theme-text-event-green,
.theme-text-event-teal,
.theme-text-event-blue,
.theme-text-event-lavender,
.theme-text-event-pink,
.theme-text-event-gray {
  color: #000000;
}

html[data-theme="dark"] .theme-text-event-peach,
html[data-theme="dark"] .theme-text-event-yellow,
html[data-theme="dark"] .theme-text-event-green,
html[data-theme="dark"] .theme-text-event-teal,
html[data-theme="dark"] .theme-text-event-blue,
html[data-theme="dark"] .theme-text-event-lavender,
html[data-theme="dark"] .theme-text-event-pink,
html[data-theme="dark"] .theme-text-event-gray {
  color: #000000;
}

html[data-theme="dark"] .event-name,
html[data-theme="dark"] .gateway-name {
  color: #000000;
}

html[data-theme="dark"] .event-select-dropdown .event-select-item-dark,
html[data-theme="dark"] .event-select-dropdown .event-select-item-dark .v-list-item-title {
  color: #000000;
}

[data-theme="dark"] .v-btn-group--density-default.v-btn-group.v-btn-toggle .v-btn:not(.v-btn--active) .v-btn__content {
  color: #ffffff;
}

.theme-bg-card-purple {
  background-color: rgb(var(--v-theme-card-purple));
}

.theme-bg-card-orange {
  background-color: rgb(var(--v-theme-card-orange));
}

.theme-bg-card-sky {
  background-color: rgb(var(--v-theme-card-sky));
}

.theme-bg-card-mint {
  background-color: rgb(var(--v-theme-card-mint));
}

.theme-bg-card-coral {
  background-color: rgb(var(--v-theme-card-coral));
}

.theme-bg-card-orchid {
  background-color: rgb(var(--v-theme-card-orchid));
}

.theme-bg-card-yellow {
  background-color: rgb(var(--v-theme-card-yellow));
}

.theme-bg-card-navy {
  background-color: rgb(var(--v-theme-card-navy));
}

.theme-bg-card-forest {
  background-color: rgb(var(--v-theme-card-forest));
}

.theme-bg-card-rose {
  background-color: rgb(var(--v-theme-card-rose));
}
</style>
